/**
 * MFX OS chain — core runtime: controlled display IDs, append-only events,
 * outbox, live hooks, metadata helpers.
 *
 * IDs: client-side Firestore transaction on `osCounters` (distinct from the
 * server-only legacy `counters`). When the command layer moves into Cloud
 * Functions this swaps to the existing /api/nextSequence without changing
 * call sites.
 */
import { useEffect, useState } from 'react';
import {
  collection, doc, getDoc, onSnapshot, query, runTransaction,
  addDoc, where, orderBy, limit as qLimit, type QueryConstraint,
} from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import type { Actor, Meta, OsEvent, OutboxEvent } from './types';

export const nowIso = () => new Date().toISOString();

export function actor(): Actor {
  const u = auth.currentUser;
  return {
    uid: u?.uid || 'unknown',
    email: (u?.email || 'unknown').toLowerCase(),
    name: u?.displayName || u?.email || 'Unknown',
  };
}

/** Fresh metadata block for a new controlled record. */
export function newMeta(displayId: string, status: string, extra?: Partial<Meta>): Meta {
  const a = actor();
  const t = nowIso();
  return {
    displayId, status,
    createdAt: t, createdBy: a.email,
    updatedAt: t, updatedBy: a.email,
    version: 1, isArchived: false, source: 'mfx-os',
    ...extra,
  };
}

/** Update-patch that bumps version + stamps the actor. */
export function touch(patch: Record<string, unknown> = {}): Record<string, unknown> {
  const a = actor();
  return { ...patch, updatedAt: nowIso(), updatedBy: a.email };
}

// ── controlled display IDs ───────────────────────────────────────────────
const PAD = (n: number, w: number) => String(n).padStart(w, '0');

export type IdKind = 'PO' | 'SO' | 'JT' | 'ITM';

/** PO-2026-0001 · SO-2026-0001 · JT-2026-0001 · ITM-0001 (transactional). */
export async function nextDisplayId(kind: IdKind): Promise<string> {
  const year = new Date().getFullYear();
  const key = kind === 'ITM' ? 'itm' : `${kind.toLowerCase()}-${year}`;
  const ref = doc(db, 'osCounters', key);
  const seq = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur = (snap.exists() ? Number(snap.data().seq) : 0) || 0;
    const next = cur + 1;
    tx.set(ref, { seq: next, kind, year, updatedAt: nowIso(), updatedBy: actor().email }, { merge: true });
    return next;
  });
  return kind === 'ITM' ? `ITM-${PAD(seq, 4)}` : `${kind}-${year}-${PAD(seq, 4)}`;
}

// ── append-only event log ────────────────────────────────────────────────
export async function appendEvent(e: Omit<OsEvent, 'actor' | 'at'>): Promise<void> {
  const ev: OsEvent = { ...e, actor: actor(), at: nowIso() };
  await addDoc(collection(db, 'osEvents'), ev as unknown as Record<string, unknown>);
}

export async function enqueueOutbox(type: string, payload: Record<string, unknown>): Promise<void> {
  const ob: OutboxEvent = { type, payload, status: 'pending', createdAt: nowIso(), createdBy: actor().email };
  await addDoc(collection(db, 'eventOutbox'), ob as unknown as Record<string, unknown>);
}

// ── live hooks ───────────────────────────────────────────────────────────
export interface WithId { id: string }

export interface ListOpts {
  wheres?: [string, '==' | '>' | '<' | '>=' | '<=' | '!=', unknown][];
  order?: [string, 'asc' | 'desc'];
  limit?: number;
}

/** Live collection listener. Falls back to unordered on index errors. */
export function useOsList<T>(coll: string, opts: ListOpts = {}, deps: unknown[] = []): { rows: (T & WithId)[]; loading: boolean; error: string } {
  const [rows, setRows] = useState<(T & WithId)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const cons: QueryConstraint[] = [];
    for (const [f, op, v] of opts.wheres || []) cons.push(where(f, op, v));
    if (opts.order) cons.push(orderBy(opts.order[0], opts.order[1]));
    cons.push(qLimit(opts.limit || 500));
    let unsub = () => {};
    const attach = (useOrder: boolean) => {
      const cs = useOrder ? cons : cons.filter((c) => c.type !== 'orderBy');
      unsub = onSnapshot(
        query(collection(db, coll), ...cs),
        (snap) => {
          let r = snap.docs.map((d) => ({ id: d.id, ...(d.data() as T) }));
          if (!useOrder && opts.order) {
            const [f, dir] = opts.order;
            r = [...r].sort((a, b) => {
              const av = String((a as Record<string, unknown>)[f] ?? '');
              const bv = String((b as Record<string, unknown>)[f] ?? '');
              return dir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
            });
          }
          setRows(r); setLoading(false); setError('');
        },
        (err) => {
          if (useOrder && String(err?.message || '').includes('index')) { attach(false); return; }
          setError(err?.message || 'load failed'); setLoading(false);
        },
      );
    };
    attach(true);
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { rows, loading, error };
}

/** Live single-doc listener. */
export function useOsDoc<T>(coll: string, id: string | null): { data: (T & WithId) | null; loading: boolean } {
  const [data, setData] = useState<(T & WithId) | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!id) { setData(null); setLoading(false); return; }
    setLoading(true);
    const unsub = onSnapshot(doc(db, coll, id), (snap) => {
      setData(snap.exists() ? ({ id: snap.id, ...(snap.data() as T) }) : null);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [coll, id]);
  return { data, loading };
}

/** One-shot doc fetch. */
export async function getOsDoc<T>(coll: string, id: string): Promise<(T & WithId) | null> {
  const snap = await getDoc(doc(db, coll, id));
  return snap.exists() ? ({ id: snap.id, ...(snap.data() as T) }) : null;
}

// ── tiny format helpers shared by OS pages ───────────────────────────────
export const usd = (n: number) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function ago(iso?: string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '—';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : iso;
};
